/**
 * Accesos → Grupos → one grupo → **Experiencia** (Plan J, J4 — mockup 4).
 *
 * WHAT A MEMBER STARTS WITH — AND NOTHING ELSE
 * -------------------------------------------
 * `groups.experience_json` is what a member is provisioned with, and it
 * **grants nothing**. A tile provisioned onto somebody's board is still
 * resolved against that person's own permissions and scope on every
 * single read and comes back `access="denied"` when they cannot open it.
 * Provisioning is about what a person STARTS with, never about what they
 * may see — which is why this tab can be edited without re-reading the
 * Contenido matrix.
 *
 * ONE HALF IS ON SCREEN, AND THE OTHER ONE IS NOT. THAT IS THE POINT
 * ------------------------------------------------------------------
 * The column has two halves — `board` and `mail` — and only the board is
 * edited here.
 *
 * * **The board is complete, end to end.** `service_groups.parse_experience`
 *   validates it here, and `home_board_service` provisions a member's
 *   board from it on their first read and re-syncs it afterwards,
 *   keeping every tile the person added themselves. Provenance is the
 *   widget ID and is DERIVED, never stored, so a tile whose id is in
 *   this template is the group's and every other tile is theirs.
 * * **The mail has no reader anywhere, so it has no form here.** Grep
 *   the tree: `experience_json` is read in exactly one place
 *   (`home_board_service._groups_of`) and that reader looks at `board`.
 *   Nothing writes a member's schedule row from `mail` — that needs
 *   `user_report_schedules.origin`, which does not exist, and belongs
 *   beside the schedules API in the portal.
 *
 *   This tab used to render the mail form anyway, under a callout saying
 *   no mail goes out. That is the shape this screen must not have: the
 *   switch was labelled *"this group HAS a default e-mail"*, which is a
 *   statement of fact, and an administrator who flips it, fills in a cron
 *   and reads a 200 has been told they scheduled something. A caveat
 *   above a form does not undo the form. It is the same rule
 *   `GroupAiConfig` states one file over about its own keys — *"a control
 *   that saves and is never looked at is worse than one that errors,
 *   because it never tells anybody"* — applied to this half.
 *
 *   The SCHEMA stays (`GroupMailTemplate`, validated, `extra="forbid"`),
 *   so a document written by hand is still normalised and still refused
 *   when it is wrong, and so J4.6's writer has something to read on the
 *   day it exists. What is gone is the claim that authoring one does
 *   anything. See `submit` for how a stored one survives a save here.
 *
 * WHY THE BOARD IS A DOCUMENT AND NOT A CANVAS
 * --------------------------------------------
 * The plan asks for *"«Editar plantilla» = board editor in template
 * mode"*. That editor is `apps/hp-portal/frontend/src/pages/home/` — a
 * different application, with drag-and-drop, a widget picker and the
 * report engine behind it — and the builder has no board canvas to open
 * in any mode. So the template is edited as the document it is, and the
 * screen earns that with the two things a canvas would have given:
 *
 *   1. a **true preview**, not a mock-up of one. It runs the SAME grid
 *      pipeline the member's board will run — `placeItems` →
 *      `stretchToFill` from `@pervasivemind/dashboard-render`, imported,
 *      never re-implemented — so what is drawn here is where the tiles
 *      land, including the vertical stretch. (The `try/catch` around it
 *      mirrors the portal's `board-layout.ts` wrapper in shape; the RULE
 *      — the packing — is imported, and a throw is rendered as one error
 *      card rather than crashing the tab.)
 *   2. the server's own refusals, verbatim. A tile with no id, two tiles
 *      under one id, an image tile, more than the cap: each comes back
 *      as a sentence naming the tile and the reason.
 *
 * ABSENT IS NOT EMPTY, AND THIS TAB WRITES THE WHOLE DOCUMENT
 * ----------------------------------------------------------
 * `parse_experience` writes the profile WHOLE — *"there is no half-edit
 * to merge and no way for two screens to interleave a template"* — so a
 * PATCH carrying only the half on screen would CLEAR the other half.
 * This tab therefore builds its save from the LOADED document and not
 * from the form alone: the board comes from the editor, and `mail` — the
 * half with no form — is copied across byte for byte. A screen that does
 * not show a document is the last screen that may delete it. `{}` clears
 * the profile and is a deliberate, separate act with its own audit row.
 *
 * THE THRESHOLD ALERT THE PLAN ASKS FOR IS NOT A GROUP SETTING
 * ------------------------------------------------------------
 * Spec §8.3's "alerts under scope" is about `delivery_scheduler` opening
 * the OWNER's scope file when it evaluates a person's own threshold —
 * which Plan J did land — and not about a dial on a group. A threshold
 * carries state per row (`last_seen_version`, `threshold_state`) that
 * only means something in a schedule one person authored and can edit,
 * so it lives on that person's schedule. The tab answers the question
 * rather than offering a dial for it.
 */

import { Button, Checkbox, CodeEditor } from "@omnitok/ui";
import {
  Callout,
  DataTable,
  Section,
  SectionStack,
  StatusChip,
  type Column,
} from "@pervasivemind/admin-ui";
import {
  placeItems,
  stretchToFill,
  type Placement,
  type PlacementInput,
} from "@pervasivemind/dashboard-render";
import { LayoutTemplate } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useT } from "@/i18n";
import {
  MAX_TEMPLATE_TILES,
  type GroupExperience,
  type GroupOut,
} from "@/lib/access-api";

// ── the board document ───────────────────────────────────────────────

/** One tile, as much of it as the builder is entitled to know. The
 *  portal's `schemas/home_board.py` is the authority for the rest and is
 *  deliberately not mirrored here. */
export interface TileSummary {
  /** Index in the stored array. It is the identity the SERVER's
   *  refusals use — *"tile 2 of the board template has no id"* — so it
   *  is on screen, and it is also the row key, because a tile with no
   *  id has nothing else to be keyed by. */
  position: number;
  id: string;
  kind: string;
  title: string | null;
  width: number;
  height: number;
  /** `report_key` when the tile has one — the only other field this
   *  screen shows, because it is what tells two KPI tiles apart. */
  source: string | null;
}

/** Read a stored widget as far as the shared grid simulator needs, and
 *  no further. Anything missing stays `""`/`NaN` and is reported by the
 *  table rather than defaulted into a plausible tile. */
export function summariseTile(
  widget: Record<string, unknown>,
  position: number,
): TileSummary {
  const str = (v: unknown): string | null =>
    typeof v === "string" && v.trim() !== "" ? v : null;
  const num = (v: unknown): number =>
    typeof v === "number" && Number.isFinite(v) ? v : Number.NaN;
  return {
    position,
    id: str(widget["id"]) ?? "",
    kind: str(widget["kind"]) ?? "",
    title: str(widget["title"]),
    width: num(widget["width"]),
    height: num(widget["height"]),
    source: str(widget["report_key"]) ?? str(widget["project_slug"]) ?? null,
  };
}

export interface TemplateLayout {
  placements: Placement[] | null;
  error: string | null;
}

/**
 * Array order → grid placements, through the SHARED simulator.
 *
 * `placeItems` + `stretchToFill` are imported from
 * `@pervasivemind/dashboard-render` and never re-implemented — the
 * board's whole layout model is "the array order IS the layout", which
 * is exactly what that simulator encodes, and it is the same code the
 * member's board will run. A throw (duplicate id, a width that bypassed
 * validation) is caught and returned as text so the tab renders one
 * error card instead of blanking; it is never swallowed and never
 * clamped.
 */
export function templateLayout(
  tiles: readonly TileSummary[],
): TemplateLayout {
  const inputs: PlacementInput[] = tiles.map((tile) => ({
    key: tile.id,
    width: tile.width,
    height: tile.height,
  }));
  try {
    return { placements: stretchToFill(placeItems(inputs)), error: null };
  } catch (err) {
    return {
      placements: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

/** Parse the editor's text. `null` document means it is not JSON yet;
 *  the caller reports that and saves nothing. */
export function parseWidgets(raw: string): {
  widgets: Record<string, unknown>[] | null;
  error: string | null;
} {
  const trimmed = raw.trim();
  if (trimmed === "") return { widgets: [], error: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return {
      widgets: null,
      error: err instanceof Error ? err.message : String(err),
    };
  }
  if (!Array.isArray(parsed)) {
    return { widgets: null, error: "notAnArray" };
  }
  const bad = parsed.findIndex(
    (w) => typeof w !== "object" || w === null || Array.isArray(w),
  );
  if (bad >= 0) return { widgets: null, error: `notAnObject:${bad}` };
  return { widgets: parsed as Record<string, unknown>[], error: null };
}

// ── the preview ──────────────────────────────────────────────────────

/** The board a member of this group is provisioned with, at the
 *  placement they will actually get. Twelve columns, one band per row
 *  unit, the tile's kind and title inside it. */
export function BoardPreview({
  tiles,
}: {
  tiles: readonly TileSummary[];
}): JSX.Element {
  const { t } = useT();
  const layout = useMemo(() => templateLayout(tiles), [tiles]);
  const byKey = useMemo(() => {
    const map = new Map<string, Placement>();
    for (const p of layout.placements ?? []) map.set(p.key, p);
    return map;
  }, [layout.placements]);

  if (layout.error !== null) {
    return (
      <Callout tone="danger" title={t("access.experience.board.layoutError")}>
        <span className="whitespace-pre-wrap">{layout.error}</span>
      </Callout>
    );
  }
  if (tiles.length === 0) {
    return (
      <p className="text-sm text-neutral-600">
        {t("access.experience.board.previewEmpty")}
      </p>
    );
  }

  return (
    <div
      className="grid gap-2"
      style={{
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gridAutoRows: "28px",
      }}
      aria-label={t("access.experience.board.preview")}
    >
      {tiles.map((tile) => {
        const placement = byKey.get(tile.id);
        if (placement === undefined) return null;
        return (
          <div
            key={tile.id}
            className="min-w-0 overflow-hidden rounded-md border border-neutral-200 bg-neutral-50 p-2"
            style={{
              gridColumn: `${placement.colStart} / span ${placement.colSpan}`,
              gridRow: `${placement.rowStart} / span ${placement.rowSpan}`,
            }}
            title={tile.id}
          >
            <div className="truncate text-[13px] font-semibold text-neutral-700">
              {tile.kind}
            </div>
            {tile.title !== null && (
              <div className="truncate text-[13px] text-neutral-600">
                {tile.title}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── what a save sends ────────────────────────────────────────────────

/**
 * The PATCH body, built from the loaded document and the board editor.
 *
 * A function with a test rather than three lines inside `submit`,
 * because the interesting half of it is invisible on screen.
 * `parse_experience` REPLACES the column with what arrives — *"there is
 * no half-edit to merge"* — and `mail` has no form on this tab, so the
 * only thing standing between a hand-written mail template and deletion
 * by somebody who came to move a tile is the `stored.mail` line below.
 * A screen that does not show a document is the last screen that may
 * delete it, and a bug there would be silent on both sides: the PATCH
 * succeeds, the tab re-renders identically, and the template is gone.
 *
 * `hadBoard` is why an EMPTY board still writes `board: {…}` when one
 * was stored: emptying the template is a real edit ("provision nobody
 * with anything") and must not be read as "this form has nothing to
 * say about boards". With nothing stored and nothing typed the key is
 * omitted instead, so a group that never had a board keeps a `{}`
 * document rather than growing an empty one.
 */
export function experienceToSave(
  widgets: Record<string, unknown>[],
  resync: boolean,
  stored: GroupExperience,
): GroupExperience {
  const next: GroupExperience = {};
  if (widgets.length > 0 || stored.board != null) {
    next.board = { widgets, resync };
  }
  if (stored.mail != null) {
    next.mail = stored.mail;
  }
  return next;
}

// ── the tab ──────────────────────────────────────────────────────────

export function GroupExperienceTab({
  group,
  saving,
  onSave,
}: {
  group: GroupOut;
  saving: boolean;
  onSave: (experience: GroupExperience) => Promise<void>;
}): JSX.Element {
  const { t } = useT();
  const stored = group.experience_json;

  const [widgetsText, setWidgetsText] = useState(() =>
    JSON.stringify(stored.board?.widgets ?? [], null, 2),
  );
  const [resync, setResync] = useState(stored.board?.resync ?? true);

  // Re-seed from the server after every save/reload. The stored document
  // is the source: this screen renders back exactly what it will edit,
  // defaults included, because `group_detail` re-validates the column on
  // the way out.
  useEffect(() => {
    setWidgetsText(JSON.stringify(stored.board?.widgets ?? [], null, 2));
    setResync(stored.board?.resync ?? true);
  }, [stored]);

  const parsed = parseWidgets(widgetsText);
  // Not memoised: the cap is 40 tiles and the mapping is field reads.
  // A memo here would need `parsed`, which is rebuilt every render, so
  // it would be a dependency that never matches — machinery that looks
  // like caching and caches nothing.
  const tiles = (parsed.widgets ?? []).map((w, i) => summariseTile(w, i));
  const overCap =
    parsed.widgets !== null && parsed.widgets.length > MAX_TEMPLATE_TILES;

  const jsonError =
    parsed.error === null
      ? null
      : parsed.error === "notAnArray"
        ? t("access.experience.board.notAnArray")
        : parsed.error.startsWith("notAnObject:")
          ? t("access.experience.board.notAnObject", {
              position: parsed.error.slice("notAnObject:".length),
            })
          : t("access.experience.board.badJson", { detail: parsed.error });

  const canSave =
    !saving && parsed.widgets !== null && !overCap && jsonError === null;

  const submit = (): void => {
    if (parsed.widgets === null) return;
    void onSave(experienceToSave(parsed.widgets, resync, stored));
  };

  const clear = (): void => {
    void onSave({});
  };

  const tileColumns: Column<TileSummary>[] = [
    {
      id: "position",
      header: t("access.experience.board.col.position"),
      width: "w-20",
      align: "right",
      headerTitle: t("access.experience.board.positionHint"),
      cell: (row) => (
        <span className="text-[13px] text-neutral-600">{row.position}</span>
      ),
    },
    {
      id: "kind",
      header: t("access.experience.board.col.kind"),
      width: "w-40",
      cell: (row) =>
        row.kind === "" ? (
          <StatusChip
            label={t("access.experience.board.noKind")}
            tone="danger"
            title={t("access.experience.board.noKindHint")}
          />
        ) : (
          <span className="font-mono text-[13px] text-neutral-700">
            {row.kind}
          </span>
        ),
    },
    {
      id: "title",
      header: t("access.experience.board.col.title"),
      cell: (row) => (
        <span className="block truncate text-sm text-neutral-900">
          {row.title ?? t("access.experience.board.noTitle")}
        </span>
      ),
    },
    {
      id: "source",
      header: t("access.experience.board.col.source"),
      width: "w-56",
      cell: (row) => (
        <span className="block truncate font-mono text-[13px] text-neutral-600">
          {row.source ?? "—"}
        </span>
      ),
    },
    {
      id: "size",
      header: t("access.experience.board.col.size"),
      width: "w-32",
      align: "right",
      cell: (row) =>
        Number.isFinite(row.width) && Number.isFinite(row.height) ? (
          <span className="text-[13px] text-neutral-700">
            {row.width} × {row.height}
          </span>
        ) : (
          <StatusChip
            label={t("access.experience.board.badSize")}
            tone="danger"
            title={t("access.experience.board.badSizeHint")}
          />
        ),
    },
    {
      id: "id",
      header: t("access.experience.board.col.id"),
      width: "w-72",
      cell: (row) =>
        row.id === "" ? (
          <StatusChip
            label={t("access.experience.board.noId")}
            tone="danger"
            title={t("access.experience.board.noIdHint")}
          />
        ) : (
          <span className="block truncate font-mono text-[13px] text-neutral-500">
            {row.id}
          </span>
        ),
    },
  ];

  return (
    <SectionStack>
      <Callout tone="info" title={t("access.experience.grantsNothingTitle")}>
        {t("access.experience.grantsNothing")}
      </Callout>

      {/* ── the Home board ── */}
      <Section
        title={t("access.experience.board.title")}
        description={t("access.experience.board.desc")}
      >
        <Checkbox
          toggle
          label={t("access.experience.board.resync")}
          description={t("access.experience.board.resyncHelp")}
          checked={resync}
          disabled={saving}
          onCheckedChange={setResync}
        />

        <div className="mt-4">
          <CodeEditor
            label={t("access.experience.board.document")}
            language="json"
            value={widgetsText}
            fullWidth
            minHeight={200}
            showLineNumbers
            disabled={saving}
            onValueChange={setWidgetsText}
            helperText={t("access.experience.board.documentHelp", {
              max: MAX_TEMPLATE_TILES,
            })}
            error={jsonError ?? undefined}
          />
        </div>

        <p className="mt-2 text-[13px] text-neutral-700">
          {t("access.experience.board.count", {
            n: parsed.widgets?.length ?? 0,
            max: MAX_TEMPLATE_TILES,
          })}
        </p>
        {overCap && (
          <Callout tone="danger" title={t("access.experience.board.overCapTitle")}>
            {t("access.experience.board.overCap", { max: MAX_TEMPLATE_TILES })}
          </Callout>
        )}

        <div className="mt-4">
          <p className="text-sm font-semibold text-neutral-900">
            {t("access.experience.board.preview")}
          </p>
          <p className="mb-2 text-[13px] text-neutral-600">
            {t("access.experience.board.previewHelp")}
          </p>
          <BoardPreview tiles={tiles} />
        </div>

        <div className="mt-4">
          <DataTable
            columns={tileColumns}
            rows={tiles}
            // Keyed by POSITION and not by id: a template whose tiles
            // repeat an id, or carry none, is exactly the document this
            // table exists to show, and a React key collision would
            // blank the rows that prove it.
            rowKey={(row) => String(row.position)}
            caption={t("access.experience.board.title")}
            empty={
              <p className="text-sm text-neutral-600">
                {t("access.experience.board.noTiles")}
              </p>
            }
          />
        </div>
      </Section>

      {/* ── the mail: the answer, not a form ── */}
      <Section
        title={t("access.experience.mail.title")}
        description={t("access.experience.mail.desc")}
      >
        <p className="text-sm text-neutral-700">
          {t("access.experience.mail.body")}
        </p>
        {/* A hand-written document is the one thing this screen must not
            hide: `submit` carries it through untouched, but «Clear the
            profile» does delete it, so the operator has to be able to see
            that it is there before pressing that. Rendered only when the
            column actually holds one — an unconditional line would be a
            warning about a document that does not exist. */}
        {stored.mail != null && (
          <Callout
            tone="warning"
            title={t("access.experience.mail.storedTitle")}
          >
            {t("access.experience.mail.stored")}
          </Callout>
        )}
      </Section>

      {/* ── thresholds: the answer, not the dial ── */}
      <Section
        title={t("access.experience.threshold.title")}
        description={t("access.experience.threshold.desc")}
      >
        <p className="text-sm text-neutral-700">
          {t("access.experience.threshold.body")}
        </p>
      </Section>

      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          ghost
          disabled={saving}
          onClick={clear}
        >
          {t("access.experience.clear")}
        </Button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<LayoutTemplate size={14} />}
          disabled={!canSave}
          onClick={submit}
        >
          {t("access.experience.save")}
        </Button>
      </div>
    </SectionStack>
  );
}
